import type { MatrixClient, User } from "matrix-js-sdk";
import { UserEvent } from "matrix-js-sdk";
import { readable } from "svelte/store";
import type { PictureStore } from "../../../Stores/PictureStore";

type MatrixMediaVariant = {
    width?: number;
    height?: number;
    resizeMethod?: "crop" | "scale";
};

const MAX_CACHED_MEDIA = 200;
const DOWNLOAD_URL_REVOKE_DELAY_MS = 30_000;
const AVATAR_RESOLUTION_MAX_ATTEMPTS = 5;
const AVATAR_RESOLUTION_RETRY_DELAY_MS = 2_000;

/**
 * Resolves Matrix MXC media through authenticated fetch and returns browser-safe object URLs.
 */
export class MatrixMediaResolver {
    private static readonly instances = new WeakMap<MatrixClient, MatrixMediaResolver>();

    private readonly resolvedUrlCache = new Map<string, string>();
    private readonly pendingResolutions = new Map<string, Promise<string | undefined>>();
    private readonly cacheOrder: string[] = [];

    private constructor(private readonly client: MatrixClient) {}

    public static forClient(client: MatrixClient): MatrixMediaResolver {
        const existing = this.instances.get(client);
        if (existing) {
            return existing;
        }
        const created = new MatrixMediaResolver(client);
        this.instances.set(client, created);
        return created;
    }

    public async resolveDisplayUrl(
        sourceUrl: string | undefined,
        variant?: MatrixMediaVariant,
        allowUnauthenticatedFallback = true
    ): Promise<string | undefined> {
        if (!sourceUrl) {
            return undefined;
        }

        if (!this.isMxcUrl(sourceUrl)) {
            return sourceUrl;
        }

        const cacheKey = this.makeCacheKey(sourceUrl, variant);
        const cached = this.resolvedUrlCache.get(cacheKey);
        if (cached) {
            this.bumpCacheKey(cacheKey);
            return cached;
        }

        const pending = this.pendingResolutions.get(cacheKey);
        if (pending) {
            return pending;
        }

        const resolution = this.fetchMediaAsObjectUrl(sourceUrl, variant)
            .then((resolvedUrl) => {
                if (resolvedUrl) {
                    this.setCachedUrl(cacheKey, resolvedUrl);
                }
                return resolvedUrl;
            })
            .catch((error) => {
                console.error("Failed to resolve authenticated Matrix media URL", error);
                if (!allowUnauthenticatedFallback) {
                    return undefined;
                }
                return this.getUnauthenticatedMediaUrl(sourceUrl, variant);
            })
            .finally(() => {
                this.pendingResolutions.delete(cacheKey);
            });

        this.pendingResolutions.set(cacheKey, resolution);
        return resolution;
    }

    public async resolveAvatarDisplayUrl(sourceUrl: string | undefined): Promise<string | undefined> {
        if (!sourceUrl) {
            return undefined;
        }

        if (!this.isMxcUrl(sourceUrl)) {
            return sourceUrl;
        }

        const cacheKey = `${sourceUrl}|avatar-raw-v1`;
        const cached = this.resolvedUrlCache.get(cacheKey);
        if (cached) {
            this.bumpCacheKey(cacheKey);
            return cached;
        }

        const pending = this.pendingResolutions.get(cacheKey);
        if (pending) {
            return pending;
        }

        const resolution = this.fetchMediaAsObjectUrl(sourceUrl)
            .then((resolvedUrl) => {
                if (resolvedUrl) {
                    this.setCachedUrl(cacheKey, resolvedUrl);
                }
                return resolvedUrl;
            })
            .catch((error) => {
                console.error("Failed to resolve Matrix avatar media URL", error);
                return undefined;
            })
            .finally(() => {
                this.pendingResolutions.delete(cacheKey);
            });

        this.pendingResolutions.set(cacheKey, resolution);
        return resolution;
    }

    public async downloadMedia(sourceUrl: string | undefined, fileName?: string): Promise<void> {
        if (!sourceUrl) {
            return;
        }

        if (!this.isMxcUrl(sourceUrl)) {
            this.triggerDownload(sourceUrl, fileName);
            return;
        }

        try {
            const mediaBlob = await this.fetchMediaBlob(sourceUrl);
            const objectUrl = URL.createObjectURL(mediaBlob);
            this.triggerDownload(objectUrl, fileName);
            globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), DOWNLOAD_URL_REVOKE_DELAY_MS);
        } catch (error) {
            console.error("Failed to download authenticated Matrix media", error);
            const unauthenticatedUrl = this.getUnauthenticatedMediaUrl(sourceUrl);
            if (unauthenticatedUrl) {
                this.triggerDownload(unauthenticatedUrl, fileName);
            }
        }
    }

    public clear(): void {
        this.pendingResolutions.clear();
        for (const url of this.resolvedUrlCache.values()) {
            URL.revokeObjectURL(url);
        }
        this.resolvedUrlCache.clear();
        this.cacheOrder.length = 0;
    }

    private async fetchMediaAsObjectUrl(sourceUrl: string, variant?: MatrixMediaVariant): Promise<string | undefined> {
        const mediaBlob = await this.fetchMediaBlob(sourceUrl, variant);
        return URL.createObjectURL(mediaBlob);
    }

    private async fetchMediaBlob(sourceUrl: string, variant?: MatrixMediaVariant): Promise<Blob> {
        const accessToken = this.client.getAccessToken();
        if (!accessToken) {
            throw new Error("Missing Matrix access token for authenticated media request");
        }

        const candidateUrls = this.getAuthenticatedMediaUrls(sourceUrl, variant);
        if (candidateUrls.length === 0) {
            throw new Error(`Unable to create HTTP media URL from source ${sourceUrl}`);
        }

        let lastFailureReason = "Unknown failure";
        for (const httpUrl of candidateUrls) {
            const response = await fetch(httpUrl, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                credentials: "omit",
            });

            if (response.ok) {
                return response.blob();
            }

            const responseBody = await this.readResponseBodySafely(response);
            lastFailureReason = `status=${response.status}${responseBody ? ` body=${responseBody}` : ""}`;

            if (this.isEndpointUnsupported(response.status, responseBody)) {
                continue;
            }

            throw new Error(`Authenticated media request failed (${lastFailureReason})`);
        }

        throw new Error(`Authenticated media request failed for all endpoint variants (${lastFailureReason})`);
    }

    private getAuthenticatedMediaUrls(sourceUrl: string, variant?: MatrixMediaVariant): string[] {
        const urls: string[] = [];
        const parsedMxc = this.parseMxcUrl(sourceUrl);
        if (!parsedMxc) {
            return urls;
        }

        const { serverName, mediaId, fragment } = parsedMxc;
        const endpointType = variant?.width || variant?.height || variant?.resizeMethod ? "thumbnail" : "download";
        const params = new URLSearchParams();

        if (variant?.width) {
            params.set("width", Math.round(variant.width).toString());
        }
        if (variant?.height) {
            params.set("height", Math.round(variant.height).toString());
        }
        if (variant?.resizeMethod) {
            params.set("method", variant.resizeMethod);
        }
        params.set("allow_redirect", "true");

        const baseUrl = this.client.getHomeserverUrl();
        const encodedServerName = encodeURIComponent(serverName);
        const encodedMediaId = encodeURIComponent(mediaId);
        const queryString = params.toString();
        const queryPart = queryString.length > 0 ? `?${queryString}` : "";
        const suffix = `${encodedServerName}/${encodedMediaId}${queryPart}${fragment}`;

        urls.push(`${baseUrl}/_matrix/client/v1/media/${endpointType}/${suffix}`);
        urls.push(`${baseUrl}/_matrix/client/unstable/org.matrix.msc3916/media/${endpointType}/${suffix}`);
        urls.push(`${baseUrl}/_matrix/client/r0/media/${endpointType}/${suffix}`);
        urls.push(`${baseUrl}/_matrix/media/v3/${endpointType}/${suffix}`);

        const sdkUrl = this.client.mxcUrlToHttp(
            sourceUrl,
            variant?.width,
            variant?.height,
            variant?.resizeMethod,
            false,
            true,
            true
        );
        if (sdkUrl) {
            urls.push(sdkUrl);
        }

        return Array.from(new Set(urls));
    }

    private parseMxcUrl(sourceUrl: string): { serverName: string; mediaId: string; fragment: string } | undefined {
        try {
            const parsed = new URL(sourceUrl);
            if (parsed.protocol !== "mxc:") {
                return undefined;
            }
            const serverName = parsed.host.trim();
            const mediaId = parsed.pathname.replace(/^\/+/, "").trim();
            if (!serverName || !mediaId) {
                return undefined;
            }
            return {
                serverName,
                mediaId,
                fragment: parsed.hash ?? "",
            };
        } catch {
            return undefined;
        }
    }

    private async readResponseBodySafely(response: Response): Promise<string> {
        try {
            const rawText = await response.text();
            return rawText.trim().slice(0, 300);
        } catch {
            return "";
        }
    }

    private isEndpointUnsupported(status: number, responseBody: string): boolean {
        if (status === 404 || status === 405) {
            return true;
        }
        if (!responseBody) {
            return false;
        }
        try {
            const parsed = JSON.parse(responseBody) as { errcode?: string };
            return parsed.errcode === "M_UNRECOGNIZED";
        } catch {
            return responseBody.includes("M_UNRECOGNIZED");
        }
    }

    private getUnauthenticatedMediaUrl(sourceUrl: string, variant?: MatrixMediaVariant): string | undefined {
        if (!this.isMxcUrl(sourceUrl)) {
            return sourceUrl;
        }

        const unauthenticatedUrl = this.client.mxcUrlToHttp(
            sourceUrl,
            variant?.width,
            variant?.height,
            variant?.resizeMethod,
            false,
            true,
            false
        );

        return unauthenticatedUrl ?? undefined;
    }

    private triggerDownload(url: string, fileName?: string): void {
        if (typeof document === "undefined") {
            return;
        }
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.rel = "noopener noreferrer";
        if (fileName) {
            anchor.download = fileName;
        }
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    }

    private makeCacheKey(sourceUrl: string, variant?: MatrixMediaVariant): string {
        return `${sourceUrl}|${variant?.width ?? ""}|${variant?.height ?? ""}|${variant?.resizeMethod ?? ""}`;
    }

    private setCachedUrl(cacheKey: string, resolvedUrl: string): void {
        const existing = this.resolvedUrlCache.get(cacheKey);
        if (existing) {
            URL.revokeObjectURL(existing);
        }

        this.resolvedUrlCache.set(cacheKey, resolvedUrl);
        this.bumpCacheKey(cacheKey);
        this.trimCache();
    }

    private bumpCacheKey(cacheKey: string): void {
        const existingIndex = this.cacheOrder.indexOf(cacheKey);
        if (existingIndex >= 0) {
            this.cacheOrder.splice(existingIndex, 1);
        }
        this.cacheOrder.push(cacheKey);
    }

    private trimCache(): void {
        while (this.cacheOrder.length > MAX_CACHED_MEDIA) {
            const oldestKey = this.cacheOrder.shift();
            if (!oldestKey) {
                return;
            }
            const url = this.resolvedUrlCache.get(oldestKey);
            if (url) {
                URL.revokeObjectURL(url);
            }
            this.resolvedUrlCache.delete(oldestKey);
        }
    }

    private isMxcUrl(url: string): boolean {
        return url.startsWith("mxc://");
    }
}

export function createMatrixAvatarPictureStore(
    client: MatrixClient,
    avatarMxcUrl: string | null | undefined,
    _size = 48
): PictureStore {
    return readable<string | undefined>(undefined, (set) => {
        let isCancelled = false;
        let attempt = 0;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        const stopRetryTimer = () => {
            if (retryTimer !== undefined) {
                clearTimeout(retryTimer);
                retryTimer = undefined;
            }
        };

        const scheduleRetry = () => {
            if (isCancelled || attempt >= AVATAR_RESOLUTION_MAX_ATTEMPTS) {
                return;
            }
            stopRetryTimer();
            retryTimer = globalThis.setTimeout(() => {
                retryTimer = undefined;
                void resolveAvatar();
            }, AVATAR_RESOLUTION_RETRY_DELAY_MS);
        };

        const resolveAvatar = async () => {
            if (!avatarMxcUrl || isCancelled) {
                return;
            }

            attempt += 1;
            const resolver = MatrixMediaResolver.forClient(client);
            try {
                const resolvedUrl = await resolver.resolveAvatarDisplayUrl(avatarMxcUrl);

                if (isCancelled) {
                    return;
                }

                if (resolvedUrl) {
                    set(resolvedUrl);
                    return;
                }
            } catch (error) {
                console.error("Failed to resolve Matrix avatar media", error);
            }

            scheduleRetry();
        };

        if (!avatarMxcUrl) {
            set(undefined);
            return () => {
                isCancelled = true;
            };
        }

        void resolveAvatar();

        return () => {
            isCancelled = true;
            stopRetryTimer();
        };
    });
}

export function createMatrixUserAvatarPictureStore(
    client: MatrixClient,
    matrixUser: User,
    _size = 48
): PictureStore {
    return readable<string | undefined>(undefined, (set) => {
        let isCancelled = false;
        let attempt = 0;
        let currentAvatarUrl = matrixUser.avatarUrl ?? undefined;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        const stopRetryTimer = () => {
            if (retryTimer !== undefined) {
                clearTimeout(retryTimer);
                retryTimer = undefined;
            }
        };

        const scheduleRetry = () => {
            if (isCancelled || attempt >= AVATAR_RESOLUTION_MAX_ATTEMPTS || !currentAvatarUrl) {
                return;
            }
            stopRetryTimer();
            retryTimer = globalThis.setTimeout(() => {
                retryTimer = undefined;
                void resolveAvatar();
            }, AVATAR_RESOLUTION_RETRY_DELAY_MS);
        };

        const resolveAvatar = async () => {
            if (isCancelled) {
                return;
            }

            if (!currentAvatarUrl) {
                set(undefined);
                return;
            }

            attempt += 1;
            const resolver = MatrixMediaResolver.forClient(client);
            try {
                const resolvedUrl = await resolver.resolveAvatarDisplayUrl(currentAvatarUrl);

                if (isCancelled) {
                    return;
                }

                if (resolvedUrl) {
                    set(resolvedUrl);
                    return;
                }
            } catch (error) {
                console.error("Failed to resolve Matrix user avatar media", error);
            }

            scheduleRetry();
        };

        const onAvatarUpdated = () => {
            currentAvatarUrl = matrixUser.avatarUrl ?? undefined;
            attempt = 0;
            stopRetryTimer();
            void resolveAvatar();
        };

        matrixUser.on(UserEvent.AvatarUrl, onAvatarUpdated);
        void resolveAvatar();

        return () => {
            isCancelled = true;
            stopRetryTimer();
            matrixUser.off(UserEvent.AvatarUrl, onAvatarUpdated);
        };
    });
}
